import { Filter } from "@/components/organisms/MyClubSidebars/Filters/Filters.type";
import { Button } from "@/design-system/Button/Button";
import { Popover } from "@/design-system/Popover/Popover";
import { tradeable as tradeableIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { FiltersState } from "@repo/shared/redux";
import { ListFilter } from "lucide-react";
import { ReactNode } from "react";

const ASSETS = import.meta.env.VITE_APP_ASSETS_BASE_URL;

const img = (src: string) => (
  <img src={src} alt="" className="size-5 object-contain" />
);

/* The icons the old filter sidebar used, grouped the way people filter. */
const GROUPS: { filters: { id: Filter; icon: ReactNode }[] }[] = [
  {
    filters: [
      {
        id: "special",
        icon: img(`${ASSETS}/fut23/cards/e_3_3.png?scale=50.png`),
      },
      { id: "gold", icon: img(`${ASSETS}/fut23/cards/e_1_3.png?scale=50.png`) },
      {
        id: "silver",
        icon: img(`${ASSETS}/fut23/cards/e_1_2.png?scale=50.png`),
      },
      {
        id: "bronze",
        icon: img(`${ASSETS}/fut23/cards/e_1_1.png?scale=50.png`),
      },
    ],
  },
  {
    filters: [
      { id: "untradeables", icon: img(`${ASSETS}/sbc-icons/untradeable.png`) },
      {
        id: "tradeables",
        icon: <span className="flex size-5 items-center">{tradeableIcon}</span>,
      },
    ],
  },
  {
    filters: [
      {
        id: "transferList",
        icon: img(`${ASSETS}/sbc-icons/transfer-list.png`),
      },
      { id: "sbcStorage", icon: img(`${ASSETS}/sbc-icons/sbc-storage.png`) },
      { id: "duplicates", icon: img(`${ASSETS}/sbc-icons/duplicate.png`) },
      {
        id: "manuallyAdded",
        icon: img(`${ASSETS}/sbc-icons/manually-added.png`),
      },
    ],
  },
];

/**
 * My Players' filters in a popover (a sheet on touch devices, as the design
 * system's Popover does): one toggle per filter, each with the icon it has
 * always had, in groups of card type, tradeability and where the card is.
 */
export const MyPlayersFilters = ({
  filters,
  onToggle,
}: {
  filters: FiltersState;
  onToggle: (filter: Filter) => void;
}) => {
  const { t } = useTranslation(["myClub", "common"]);
  const active = Object.values(filters).filter(Boolean).length;

  return (
    <Popover
      title={t("common:filters")}
      placement="bottom"
      align="end"
      width={300}
      newDesignSystem
      negLabel=""
      posLabel=""
      trigger={
        <Button variant="secondary" size="small" icon={<ListFilter />}>
          <span className="flex items-center gap-1.5">
            <span>{t("common:filters")}</span>
            {active > 0 && (
              <span className="flex size-4 items-center justify-center rounded-full bg-primary text-xs font-semibold text-on-primary tabular-nums">
                {active}
              </span>
            )}
          </span>
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {GROUPS.map((group, i) => (
          <div
            key={i}
            className={cn(
              "grid grid-cols-2 gap-1.5",
              i > 0 && "border-t border-neutral-750 pt-3",
            )}
          >
            {group.filters.map(({ id, icon }) => {
              const on = filters[id];
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => onToggle(id)}
                  className={cn(
                    "flex h-10 items-center gap-2 rounded-lg border px-2.5 text-left text-sm transition-colors",
                    on
                      ? "border-primary-border bg-primary/20 text-neutral-50"
                      : "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-750",
                  )}
                >
                  {icon}
                  <span className="truncate">{t(`players.filters.${id}`)}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </Popover>
  );
};
